import { Switch, Route, useLocation, Redirect } from "wouter";
import { MotionConfig } from "framer-motion";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { lazy, Suspense, useEffect } from "react";
import NotFound from "@/pages/not-found";
import Home from "@/pages/Home";
import Security from "@/pages/Security";
import Pricing from "@/pages/Pricing";
import { customerApi } from "./lib/customerApi";
import ScrollToTop from "./components/ScrollToTop";
import { persistReferralCode } from "./hooks/useReferralCode";
const Inheritance = lazy(() => import("@/pages/Inheritance"));
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const RecoveryPlans = lazy(() => import("@/pages/RecoveryPlans"));
const ChooseSetup = lazy(() => import("@/pages/ChooseSetup"));
const Advisers = lazy(() => import("@/pages/Advisers"));
const Partners = lazy(() => import("@/pages/Partners"));
const PartnerRef = lazy(() => import("@/pages/PartnerRef"));
const Contact = lazy(() => import("@/pages/Contact"));
const About = lazy(() => import("@/pages/About"));
const BillingSuccess = lazy(() => import("@/pages/BillingSuccess"));
const Terms = lazy(() => import("@/pages/legal/Terms"));
const Privacy = lazy(() => import("@/pages/legal/Privacy"));
const Cookies = lazy(() => import("@/pages/legal/Cookies"));
const Risk = lazy(() => import("@/pages/legal/Risk"));
const PartnerAgreement = lazy(() => import("@/pages/legal/PartnerAgreement"));
const DueDiligence = lazy(() => import("@/pages/legal/DueDiligence"));
const FindAnAdviser = lazy(() => import("@/pages/FindAnAdviser"));
const Placeholder = lazy(() => import("@/pages/Placeholder"));
const BookCall = lazy(() => import("@/pages/BookCall"));
const Compare = lazy(() => import("@/pages/Compare"));
const Watchtower = lazy(() => import("@/pages/Watchtower"));
const StatusPage = lazy(() => import("@/pages/Status"));
const InheritanceScorecard = lazy(() => import("@/pages/BitcoinInheritanceScorecard"));
const InheritanceTaxCalculator = lazy(() => import("@/pages/BitcoinInheritanceTaxCalculator"));
const CryptoInheritancePlanningUK = lazy(() => import("@/pages/CryptoInheritancePlanningUK"));
const CryptoInheritanceChecklistUK = lazy(() => import("@/pages/CryptoInheritanceChecklistUK"));
const ExecutorCryptoChecklist = lazy(() => import("@/pages/ExecutorCryptoChecklist"));
const Insights = lazy(() => import("@/pages/Insights"));
const InsightsArticle = lazy(() => import("@/pages/InsightsArticle"));

const StaffLogin = lazy(() => import("@/pages/StaffLogin"));
const StaffDashboard = lazy(() => import("@/pages/StaffDashboard"));
const StaffCustomerDetails = lazy(() => import("@/pages/StaffCustomerDetails"));
const StaffIntroducers = lazy(() => import("@/pages/StaffIntroducers"));
const StaffPlaybook = lazy(() => import("@/pages/StaffPlaybook"));
const StaffOnboarding = lazy(() => import("@/pages/StaffOnboarding"));
const StaffPolicyPack = lazy(() => import("@/pages/StaffPolicyPack"));
const StaffCustomers = lazy(() => import("@/pages/StaffCustomers"));
const StaffOnboardingRequests = lazy(() => import("@/pages/StaffOnboardingRequests"));
const StaffMarketing = lazy(() => import("@/pages/StaffMarketing"));
const StaffSettings = lazy(() => import("@/pages/StaffSettings"));
const StaffEnquiries = lazy(() => import("@/pages/StaffEnquiries"));
const StaffPartners = lazy(() => import("@/pages/StaffPartners"));
const StaffPartnerSuite = lazy(() => import("@/pages/StaffPartnerSuite"));
const StaffPdfLibrary = lazy(() => import("@/pages/StaffPdfLibrary"));

const CustomerLogin = lazy(() => import("@/pages/customer/Login"));
const CustomerDashboard = lazy(() => import("@/pages/customer/Dashboard"));
const CustomerPolicies = lazy(() => import("@/pages/customer/Policies"));
const CustomerPolicyDetail = lazy(() => import("@/pages/customer/PolicyDetail"));
const CustomerRecovery = lazy(() => import("@/pages/customer/Recovery"));
const CustomerSettings = lazy(() => import("@/pages/customer/Settings"));

const ClientLogin = lazy(() => import("@/pages/client/Login"));
const ClientInvite = lazy(() => import("@/pages/client/Invite"));
const ClientMagicLink = lazy(() => import("@/pages/client/MagicLink"));
const ClientDashboard = lazy(() => import("@/pages/client/Dashboard"));
const ClientOnboarding = lazy(() => import("@/pages/client/Onboarding"));
const ClientContacts = lazy(() => import("@/pages/client/Contacts"));
const ClientPolicies = lazy(() => import("@/pages/client/Policies"));
const ClientRecovery = lazy(() => import("@/pages/client/Recovery"));
const ClientActivity = lazy(() => import("@/pages/client/Activity"));
const ClientDocuments = lazy(() => import("@/pages/client/Documents"));
const ClientTools = lazy(() => import("@/pages/client/Tools"));
const ClientAdvancedPositions = lazy(() => import("@/pages/client/AdvancedPositions"));
const ClientAssets = lazy(() => import("@/pages/client/Assets"));
const ClientMonitoring = lazy(() => import("@/pages/client/Monitoring"));
const ClientInheritance = lazy(() => import("@/pages/client/Inheritance"));
const ClientEmailVerify = lazy(() => import("@/pages/client/EmailVerify"));
const ClientEmailVerifySuccess = lazy(() => import("@/pages/client/EmailVerifySuccess"));
const ClientEmailVerifyError = lazy(() => import("@/pages/client/EmailVerifyError"));

const RecipientInvite = lazy(() => import("@/pages/recipient/Invite"));
const RecipientLogin = lazy(() => import("@/pages/recipient/Login"));
const RecipientDashboard = lazy(() => import("@/pages/recipient/Dashboard"));

const PartnerLogin = lazy(() => import("@/pages/partner/Login"));
const PartnerDashboard = lazy(() => import("@/pages/partner/Dashboard"));
const PartnerSetPassword = lazy(() => import("@/pages/partner/SetPassword"));
const PartnerActivate = lazy(() => import("@/pages/partner/Activate"));

function PageLoader() {
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="animate-pulse text-teal-400">Loading...</div>
    </div>
  );
}

function CustomerAuthGuard({ children }: { children: React.ReactNode }) {
  const { data: auth, isLoading } = useQuery({
    queryKey: ["customerAuth"],
    queryFn: customerApi.me,
    retry: false,
  });

  if (isLoading) {
    return <PageLoader />;
  }

  if (!auth?.authenticated) {
    return <Redirect to="/app/login" />;
  }

  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        {/* Public pages with SiteLayout */}
        <Route path="/" component={Home} />
        <Route path="/security" component={Security} />
        <Route path="/inheritance" component={Inheritance} />
        <Route path="/how-it-works" component={HowItWorks} />
        <Route path="/recovery" component={RecoveryPlans} />
        <Route path="/setup" component={ChooseSetup} />
        <Route path="/introducers"><Redirect to="/partners" /></Route>
        <Route path="/advisers" component={Advisers} />
        <Route path="/find-an-adviser" component={FindAnAdviser} />
        <Route path="/partners/ref/:code" component={PartnerRef} />
        <Route path="/partners" component={Partners} />
        <Route path="/contact" component={Contact} />
        <Route path="/about" component={About} />
        <Route path="/pricing" component={Pricing} />
        <Route path="/crypto-inheritance-planning-uk" component={CryptoInheritancePlanningUK} />
        <Route path="/crypto-inheritance-checklist-uk" component={CryptoInheritanceChecklistUK} />
        <Route path="/executor-crypto-checklist" component={ExecutorCryptoChecklist} />
        <Route path="/insights" component={Insights} />
        <Route path="/insights/:slug" component={InsightsArticle} />
        <Route path="/billing/success" component={BillingSuccess} />
        <Route path="/book" component={BookCall} />
        <Route path="/compare" component={Compare} />
        <Route path="/watchtower" component={Watchtower} />
        <Route path="/bitcoin-inheritance-scorecard" component={InheritanceScorecard} />
        <Route path="/bitcoin-inheritance-tax-calculator" component={InheritanceTaxCalculator} />
        
        {/* Legal pages */}
        <Route path="/terms" component={Terms} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/cookies" component={Cookies} />
        <Route path="/risk" component={Risk} />
        <Route path="/partner-agreement" component={PartnerAgreement} />
        <Route path="/due-diligence" component={DueDiligence} />
        
        {/* Placeholder pages */}
        <Route path="/help">
          <Suspense fallback={<PageLoader />}>
            <Placeholder title="Help Centre" />
          </Suspense>
        </Route>
        <Route path="/status" component={StatusPage} />
        
        {/* Staff portal */}
        <Route path="/staff/login" component={StaffLogin} />
        <Route path="/staff/onboarding/:sessionId" component={StaffOnboarding} />
        <Route path="/staff/onboarding" component={StaffOnboarding} />
        <Route path="/staff/customers/:id" component={StaffCustomerDetails} />
        <Route path="/staff/customers" component={StaffCustomers} />
        <Route path="/staff/policy-pack/:customerId" component={StaffPolicyPack} />
        <Route path="/staff/introducers" component={StaffIntroducers} />
        <Route path="/staff/partners/:partnerId/suite" component={StaffPartnerSuite} />
        <Route path="/staff/partners" component={StaffPartners} />
        <Route path="/staff/call-requests" component={StaffOnboardingRequests} />
        <Route path="/staff/playbook" component={StaffPlaybook} />
        <Route path="/staff/marketing" component={StaffMarketing} />
        <Route path="/staff/settings" component={StaffSettings} />
        <Route path="/staff/enquiries/:id" component={StaffEnquiries} />
        <Route path="/staff/enquiries" component={StaffEnquiries} />
        <Route path="/staff/pdfs/:id" component={StaffPdfLibrary} />
        <Route path="/staff/pdfs" component={StaffPdfLibrary} />
        <Route path="/staff" component={StaffDashboard} />
        
        {/* Customer app portal */}
        <Route path="/app/login" component={CustomerLogin} />
        <Route path="/app">
          <CustomerAuthGuard>
            <CustomerDashboard />
          </CustomerAuthGuard>
        </Route>
        <Route path="/app/policies/:id">
          <CustomerAuthGuard>
            <CustomerPolicyDetail />
          </CustomerAuthGuard>
        </Route>
        <Route path="/app/policies">
          <CustomerAuthGuard>
            <CustomerPolicies />
          </CustomerAuthGuard>
        </Route>
        <Route path="/app/recovery">
          <CustomerAuthGuard>
            <CustomerRecovery />
          </CustomerAuthGuard>
        </Route>
        <Route path="/app/settings">
          <CustomerAuthGuard>
            <CustomerSettings />
          </CustomerAuthGuard>
        </Route>

        {/* Client portal */}
        <Route path="/client/login" component={ClientLogin} />
        <Route path="/client/invite/:token" component={ClientInvite} />
        <Route path="/client/magic/:token" component={ClientMagicLink} />
        <Route path="/client" component={ClientDashboard} />
        <Route path="/client/onboarding" component={ClientOnboarding} />
        <Route path="/client/contacts" component={ClientContacts} />
        <Route path="/client/contacts/verify-email/success" component={ClientEmailVerifySuccess} />
        <Route path="/client/contacts/verify-email/error" component={ClientEmailVerifyError} />
        <Route path="/client/contacts/verify-email/:token" component={ClientEmailVerify} />
        <Route path="/client/policies/:id" component={ClientPolicies} />
        <Route path="/client/policies" component={ClientPolicies} />
        <Route path="/client/recovery" component={ClientRecovery} />
        <Route path="/client/documents" component={ClientDocuments} />
        <Route path="/client/activity" component={ClientActivity} />
        <Route path="/client/tools" component={ClientTools} />
        <Route path="/client/advanced-positions" component={ClientAdvancedPositions} />
        <Route path="/client/assets" component={ClientAssets} />
        <Route path="/client/monitoring" component={ClientMonitoring} />
        <Route path="/client/inheritance" component={ClientInheritance} />
        
        {/* Recipient Portal */}
        <Route path="/recipient/invite" component={RecipientInvite} />
        <Route path="/recipient/login" component={RecipientLogin} />
        <Route path="/recipient" component={RecipientDashboard} />
        
        {/* Partner Portal */}
        <Route path="/partner/activate" component={PartnerActivate} />
        <Route path="/partner/set-password" component={PartnerSetPassword} />
        <Route path="/partner/login" component={PartnerLogin} />
        <Route path="/partner" component={PartnerDashboard} />
        
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    persistReferralCode();
  }, []);

  return (
    <MotionConfig reducedMotion="user">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <ScrollToTop />
          <Toaster />
          <Router />
        </TooltipProvider>
      </QueryClientProvider>
    </MotionConfig>
  );
}

export default App;
